import * as React from 'react';
import { HelpCircle } from 'lucide-react';

import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface HelpItem {
  /** The question, shown as the bolded term. */
  q: string;
  /** The answer — plain text or rich content. */
  a: React.ReactNode;
}

interface HelpButtonProps {
  /** Optional heading shown above the Q&A list. */
  title?: string;
  /** Q&A entries rendered in the popover. */
  items?: HelpItem[];
  /** Arbitrary content rendered after the items (or on its own). */
  children?: React.ReactNode;
  /** Optional action rendered at the foot of the popover (e.g. a link onward). */
  footer?: React.ReactNode;
  /** Accessible label for the trigger. Defaults to "Help". */
  label?: string;
  /**
   * Pin the trigger to the lower-right corner of the viewport as a floating affordance.
   * The popover then opens upward from the corner.
   */
  floating?: boolean;
  /**
   * When floating, text shown as a hover/focus tooltip on the "?" (e.g. "Need help?") — keeps the
   * corner clean (icon only) while still labelling the affordance.
   */
  floatingLabel?: string;
  /** Popover alignment relative to the trigger. */
  align?: 'start' | 'center' | 'end';
  /** Popover side relative to the trigger. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Extra classes for the trigger button. */
  className?: string;
  /** Open on mount — for always-open contexts and tests. */
  defaultOpen?: boolean;
}

const TRIGGER_BASE =
  'inline-flex items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:text-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/**
 * A "?" affordance that reveals just-in-time help in a popover. Pass `items` for a consistent Q&A
 * list and/or `children` for richer content. Lightweight by design — this is the helper-button
 * surface for onboarding, not a guided tour. With `floating`, it pins to the lower-right corner as
 * an icon-only button with an optional `floatingLabel` hover tooltip.
 */
export function HelpButton({
  title,
  items,
  children,
  footer,
  label = 'Help',
  floating = false,
  floatingLabel,
  align = 'end',
  side,
  className,
  defaultOpen,
}: HelpButtonProps) {
  const resolvedSide = side ?? (floating ? 'top' : undefined);

  const buttonEl = (
    <button
      type="button"
      aria-label={label}
      className={cn(
        TRIGGER_BASE,
        floating
          ? 'fixed bottom-6 right-6 z-40 h-10 w-10 border bg-background/95 shadow-sm backdrop-blur hover:bg-accent'
          : 'h-5 w-5 shrink-0',
        className,
      )}
    >
      <HelpCircle className={floating ? 'h-5 w-5' : 'h-4 w-4'} />
    </button>
  );

  return (
    <Popover defaultOpen={defaultOpen}>
      {floating ? (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{buttonEl}</PopoverTrigger>
            </TooltipTrigger>
            {floatingLabel && (
              // Neutral (not the brand-primary tooltip default) so the help affordance stays
              // monochrome across every subsidiary.
              <TooltipContent side="left" sideOffset={8} className="border bg-popover text-popover-foreground">
                {floatingLabel}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      ) : (
        <PopoverTrigger asChild>{buttonEl}</PopoverTrigger>
      )}
      <PopoverContent align={align} side={resolvedSide} className="w-80 text-sm">
        {title && <p className="mb-2 font-semibold text-foreground">{title}</p>}
        {items && items.length > 0 && (
          <dl className="space-y-3">
            {items.map((item, i) => (
              <div key={i}>
                <dt className="font-medium text-foreground">{item.q}</dt>
                <dd className="mt-0.5 text-muted-foreground">{item.a}</dd>
              </div>
            ))}
          </dl>
        )}
        {children}
        {footer}
      </PopoverContent>
    </Popover>
  );
}
